import { useState } from 'react';
import type {
  CreateScheduleRuleRequest,
  ScheduleRuleDto,
  UpdateScheduleRuleRequest,
} from '../../../store/useScheduleRuleStore';
import { ScheduleRuleType, Weekday, WeekdayOrdinal } from '../../../utils/scheduleRuleConstants';
import type { FacilityFlowCoreModulesPlanningEntitiesTaskScheduleRuleType } from '../../../api/generated/models';
import type { FacilityFlowCoreModulesPlanningEntitiesWeekdayOrdinal } from '../../../api/generated/models';
import type { SystemDayOfWeek } from '../../../api/generated/models';

/**
 * Shared form state for building a schedule rule. Backs both the inline editor and
 * the detail-page manager so the two stay in lockstep with the backend contract.
 *
 * Day-free variants: Weekly with no weekday selected = once per week, any day.
 * Monthly with `monthlyAnyDay` = once per calendar month; its yearly window is
 * optional (`monthlyHasWindow`), absent = all year.
 */
export interface ScheduleRuleForm {
  ruleType: number;
  setRuleType: (type: number) => void;
  weekdays: number[];
  toggleWeekday: (day: number) => void;
  monthlyAnyDay: boolean;
  setMonthlyAnyDay: (anyDay: boolean) => void;
  monthlyWeekday: number;
  setMonthlyWeekday: (day: number) => void;
  ordinal: number;
  setOrdinal: (ordinal: number) => void;
  monthlyHasWindow: boolean;
  setMonthlyHasWindow: (hasWindow: boolean) => void;
  windowFromMonth: number;
  setWindowFromMonth: (month: number) => void;
  windowFromDay: number;
  setWindowFromDay: (day: number) => void;
  windowToMonth: number;
  setWindowToMonth: (month: number) => void;
  windowToDay: number;
  setWindowToDay: (day: number) => void;

  needsWeekdays: boolean;
  needsMonthly: boolean;
  /** Window inputs visible: BiWeekly/Seasonal always, Monthly only when opted in. */
  needsWindow: boolean;
  /** Monthly: window is opt-in ("all year" by default). */
  windowOptional: boolean;

  reset: () => void;
  loadFrom: (rule: ScheduleRuleDto) => void;
  preview: () => CreateScheduleRuleRequest;
  buildCreate: () => CreateScheduleRuleRequest;
  buildUpdate: (isActive: boolean) => UpdateScheduleRuleRequest;
}

const DEFAULT_FROM_MONTH = 4;
const DEFAULT_FROM_DAY = 15;
const DEFAULT_TO_MONTH = 10;
const DEFAULT_TO_DAY = 31;

export function useScheduleRuleForm(): ScheduleRuleForm {
  const [ruleType, setRuleType] = useState<number>(ScheduleRuleType.Weekly);
  const [weekdays, setWeekdays] = useState<number[]>([]);
  const [monthlyAnyDay, setMonthlyAnyDay] = useState<boolean>(true);
  const [monthlyWeekday, setMonthlyWeekday] = useState<number>(Weekday.Monday);
  const [ordinal, setOrdinal] = useState<number>(WeekdayOrdinal.First);
  const [monthlyHasWindow, setMonthlyHasWindow] = useState<boolean>(false);
  const [windowFromMonth, setWindowFromMonth] = useState<number>(DEFAULT_FROM_MONTH);
  const [windowFromDay, setWindowFromDay] = useState<number>(DEFAULT_FROM_DAY);
  const [windowToMonth, setWindowToMonth] = useState<number>(DEFAULT_TO_MONTH);
  const [windowToDay, setWindowToDay] = useState<number>(DEFAULT_TO_DAY);

  const needsWeekdays = ruleType === ScheduleRuleType.Weekly;
  const needsMonthly = ruleType === ScheduleRuleType.Monthly;
  const windowOptional = ruleType === ScheduleRuleType.Monthly;
  const needsWindow =
    ruleType === ScheduleRuleType.BiWeekly ||
    ruleType === ScheduleRuleType.Seasonal ||
    (ruleType === ScheduleRuleType.Monthly && monthlyHasWindow);

  const toggleWeekday = (day: number) => {
    setWeekdays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day],
    );
  };

  const reset = () => {
    setRuleType(ScheduleRuleType.Weekly);
    setWeekdays([]);
    setMonthlyAnyDay(true);
    setMonthlyWeekday(Weekday.Monday);
    setOrdinal(WeekdayOrdinal.First);
    setMonthlyHasWindow(false);
    setWindowFromMonth(DEFAULT_FROM_MONTH);
    setWindowFromDay(DEFAULT_FROM_DAY);
    setWindowToMonth(DEFAULT_TO_MONTH);
    setWindowToDay(DEFAULT_TO_DAY);
  };

  const loadFrom = (rule: ScheduleRuleDto) => {
    setRuleType(rule.type ?? ScheduleRuleType.Weekly);
    setWeekdays(rule.weekdays ? [...rule.weekdays] : []);
    setMonthlyAnyDay(rule.weekday == null && rule.ordinal == null);
    setMonthlyWeekday(rule.weekday ?? Weekday.Monday);
    setOrdinal(rule.ordinal ?? WeekdayOrdinal.First);
    setMonthlyHasWindow(rule.windowFromMonth != null);
    setWindowFromMonth(rule.windowFromMonth ?? DEFAULT_FROM_MONTH);
    setWindowFromDay(rule.windowFromDay ?? DEFAULT_FROM_DAY);
    setWindowToMonth(rule.windowToMonth ?? DEFAULT_TO_MONTH);
    setWindowToDay(rule.windowToDay ?? DEFAULT_TO_DAY);
  };

  const buildCore = (): CreateScheduleRuleRequest => {
    const sendMonthlyDay = needsMonthly && !monthlyAnyDay;
    return {
      type: ruleType as FacilityFlowCoreModulesPlanningEntitiesTaskScheduleRuleType,
      weekdays: needsWeekdays ? (weekdays as SystemDayOfWeek[]) : undefined,
      weekday: sendMonthlyDay ? (monthlyWeekday as SystemDayOfWeek) : undefined,
      ordinal: sendMonthlyDay
        ? (ordinal as FacilityFlowCoreModulesPlanningEntitiesWeekdayOrdinal)
        : undefined,
      windowFromMonth: needsWindow ? windowFromMonth : null,
      windowFromDay: needsWindow ? windowFromDay : null,
      windowToMonth: needsWindow ? windowToMonth : null,
      windowToDay: needsWindow ? windowToDay : null,
    };
  };

  return {
    ruleType,
    setRuleType,
    weekdays,
    toggleWeekday,
    monthlyAnyDay,
    setMonthlyAnyDay,
    monthlyWeekday,
    setMonthlyWeekday,
    ordinal,
    setOrdinal,
    monthlyHasWindow,
    setMonthlyHasWindow,
    windowFromMonth,
    setWindowFromMonth,
    windowFromDay,
    setWindowFromDay,
    windowToMonth,
    setWindowToMonth,
    windowToDay,
    setWindowToDay,
    needsWeekdays,
    needsMonthly,
    needsWindow,
    windowOptional,
    reset,
    loadFrom,
    preview: buildCore,
    buildCreate: buildCore,
    buildUpdate: (isActive: boolean) => ({ ...buildCore(), isActive }),
  };
}
